// src/components/layout/SettingsShell.tsx
import React, { useMemo } from 'react';

import RoddoPageShell from './RoddoPageShell';
import RoddoPageHero from './RoddoPageHero';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../../constants/theme';
import type { RoddoPageToneKey } from '../../constants/pageTones';

interface SettingsShellProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  isDarkMode?: boolean;
  tone?: RoddoPageToneKey;
  kicker?: string;
  icon?: React.ReactNode;
  hideHero?: boolean;
  onBack?: () => void;
}

const inferTone = (title: string): RoddoPageToneKey => {
  const lower = title.toLowerCase();
  if (lower.includes('support')) return 'support';
  if (lower.includes('legal') || lower.includes('privacy') || lower.includes('terms')) return 'legal';
  if (lower.includes('notification')) return 'notifications';
  if (lower.includes('license')) return 'licenses';
  if (lower.includes('profile') || lower.includes('account')) return 'profile';
  return 'default';
};

const SettingsShell: React.FC<SettingsShellProps> = ({
  title,
  subtitle,
  children,
  isDarkMode,
  tone,
  kicker,
  icon,
  hideHero = false,
  onBack,
}) => {
  const dark = resolveRoddoDarkMode(isDarkMode);
  const ui = getRoddoUiTokens(dark);
  const resolvedTone = tone ?? inferTone(title);

  const contentStyle = useMemo<React.CSSProperties>(
    () => ({
      background: dark ? '#0A1120' : '#F7F2E8',
      border: `1px solid ${ui.border}`,
      boxShadow: dark
        ? '0 4px 12px rgba(0,0,0,0.12)'
        : '0 4px 12px rgba(89,65,37,0.045)',
      color: ui.textPrimary,
    }),
    [dark, ui],
  );

  return (
    <RoddoPageShell padding="none" contentClassName="max-w-3xl px-0">
      <div className="space-y-4" data-roddo-theme={dark ? 'dark' : 'light'}>
        {!hideHero ? (
          <RoddoPageHero
            tone={resolvedTone}
            kicker={kicker}
            icon={icon}
            title={title}
            subtitle={subtitle}
            isDarkMode={dark}
            compact
            topRightAction={
              onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  aria-label="Back"
                  title="Back"
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-xl font-black leading-none transition hover:-translate-y-0.5 active:translate-y-px"
                  style={{
                    background: dark ? '#101827' : 'rgba(255,255,255,0.82)',
                    border: `1px solid ${ui.borderStrong}`,
                    color: ui.textPrimary,
                    boxShadow: dark
                      ? '0 4px 12px rgba(0,0,0,0.14)'
                      : '0 5px 14px rgba(89,65,37,0.07)',
                  }}
                >
                  ‹
                </button>
              ) : null
            }
          />
        ) : null}

        <div className="rounded-[14px] px-1.5 py-2 sm:px-2 sm:py-2.5" style={contentStyle}>
          {children}
        </div>
      </div>
    </RoddoPageShell>
  );
};

export default SettingsShell;
